import { useState } from "react";

const photoLocations = [
  { name: "CVS Pharmacy — Old Town Pasadena", address: "333 S Lake Ave, Pasadena, CA 91101", hours: "Daily 8 AM – 10 PM" },
  { name: "Walgreens — Pasadena", address: "1580 E Colorado Blvd, Pasadena, CA 91106", hours: "Daily 8 AM – 10 PM" },
  { name: "FedEx Office — Pasadena", address: "140 S Lake Ave, Pasadena, CA 91101", hours: "Call for hours" },
  { name: "CVS Pharmacy — Arcadia", address: "401 E Live Oak Ave, Arcadia, CA 91006", hours: "Daily 8 AM – 10 PM" },
  { name: "The UPS Store — Pasadena", address: "680 E Colorado Blvd, Pasadena, CA 91101", hours: "Call for hours" },
  { name: "Walgreens — San Marino", address: "2299 Huntington Dr, San Marino, CA 91108", hours: "Daily 8 AM – 10 PM" },
];

const dmvLocations = [
  {
    name: "Pasadena DMV",
    address: "75 N Marengo Ave, Pasadena, CA 91101",
    hours: "Tue–Fri 8:00 AM – 5:00 PM, Sat 8:00 AM – 1:00 PM",
    note: "Driver licenses, REAL ID, and state IDs. Appointment strongly recommended. Walk-in waits can exceed 2–3 hours.",
    featured: true,
  },
  {
    name: "Arcadia DMV",
    address: "11243 E Las Tunas Dr, Temple City, CA 91780",
    hours: "Tue–Fri 8:00 AM – 5:00 PM, Sat 8:00 AM – 1:00 PM",
    note: "Serves Arcadia, Temple City, and San Gabriel Valley residents. Appointment recommended.",
    featured: false,
  },
];

export default function PasadenaHelpfulResourcesSection() {
  const [photoExpanded, setPhotoExpanded] = useState(false);
  const visiblePhotos = photoExpanded ? photoLocations : photoLocations.slice(0, 4);

  return (
    <section id="local-resources" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Helpful Resources for Pasadena Travelers
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Getting your passport ready involves more than just the application. Here are local San Gabriel Valley resources to help you gather everything you need before your next international trip.
          </p>
        </div>

        <div className="space-y-6">
          {/* Passport Photos */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-camera-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Passport Photos</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Passport Photo Locations Near Pasadena</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-4">
                Your passport application requires two identical 2&quot;×2&quot; passport photos taken within the last 6 months. Popular photo locations near Pasadena include:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                {visiblePhotos.map((loc, i) => (
                  <div key={i} className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-3.5">
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{loc.name}</p>
                    <p className="text-gray-500 text-xs leading-relaxed">{loc.address}</p>
                    <p className="text-gray-400 text-xs mt-1">{loc.hours}</p>
                  </div>
                ))}
              </div>
              {!photoExpanded && (
                <button onClick={() => setPhotoExpanded(true)} className="text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs flex items-center gap-1.5 mb-4 cursor-pointer transition-colors">
                  <i className="ri-add-circle-line text-sm"></i>Show all {photoLocations.length} photo locations
                </button>
              )}
              <div className="bg-[#F7F8FA] border border-gray-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-lightbulb-line text-[#F6C344] mr-1.5"></i>
                  <strong>Tip:</strong> Wear a plain white or off-white top, no glasses, neutral expression. Photos must meet strict U.S. State Department specifications — ask the technician to confirm compliance before you leave.
                </p>
              </div>
            </div>
          </div>

          {/* DMV */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-id-card-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Driver&apos;s License &amp; State ID</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Pasadena DMV Locations</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                A valid government-issued photo ID is required for your passport application. If you need a new or renewed California driver&apos;s license or REAL ID, these DMV locations serve the Pasadena area.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {dmvLocations.map((loc, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${loc.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-map-pin-2-line text-[#1A365D] text-base"></i>
                      </div>
                      <div>
                        <p className="font-bold text-[#1A365D] text-xs leading-snug">{loc.name}</p>
                        <p className="text-gray-500 text-xs mt-1">{loc.address}</p>
                        <p className="text-gray-400 text-xs mt-0.5">{loc.hours}</p>
                        <p className="text-[#1A365D] text-xs font-medium mt-1.5 leading-snug">{loc.note}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-calendar-check-line text-amber-500 mr-1.5"></i>
                  <strong>Appointment recommended</strong> — walk-in wait times at Pasadena DMV can be long. Book your slot online at dmv.ca.gov before visiting.
                </p>
              </div>
            </div>
          </div>

          {/* Vital Records */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-file-text-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Vital Records</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Birth, Death, Marriage &amp; Divorce Records for Pasadena</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                Certified vital records are often required for passport applications, name changes, and legal documentation. Here are the official sources for Los Angeles County and Pasadena-area residents.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the Los Angeles County Registrar-Recorder/County Clerk. Online, mail, or in-person options for Pasadena residents.", url: "https://www.lavote.gov/home/records" },
                  { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through the Los Angeles County Department of Public Health Vital Records. Required for estate and legal matters.", url: "https://www.lavote.gov/home/records" },
                  { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Los Angeles County Registrar-Recorder. Certified copies available for name changes and legal purposes in Pasadena.", url: "https://www.lavote.gov/home/records/marriage-records" },
                  { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the Los Angeles Superior Court. Required for name change passport corrections and legal documentation.", url: "https://www.lacourt.org/" },
                ].map((item, i) => (
                  <div key={i} className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <i className={`${item.icon} text-[#F6C344] text-lg`}></i>
                      <p className="font-bold text-[#1A365D] text-xs">{item.label}</p>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed mb-2">{item.desc}</p>
                    <a href={item.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                      Visit Website <i className="ri-external-link-line text-xs"></i>
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Airports */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Airports</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Airports Serving Pasadena Travelers</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {[
                  { code: "LAX", name: "Los Angeles International Airport", distance: "About 25 miles southwest of Pasadena", address: "1 World Way, Los Angeles, CA 90045", note: "Primary international gateway. Nonstop service to Tokyo, Seoul, Frankfurt, Paris, London, Mexico City, and beyond.", url: "https://www.flylax.com/", label: "LAX Airport", featured: true },
                  { code: "BUR", name: "Hollywood Burbank Airport", distance: "About 10 miles northwest of Pasadena", address: "2627 N Hollywood Way, Burbank, CA 91505", note: "More convenient for Pasadena travelers on domestic routes. Limited to primarily domestic service.", url: "https://www.burbankairport.com/", label: "Burbank Airport", featured: false },
                ].map((a, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${a.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[#1A365D] font-black text-xl leading-none">{a.code}</span>
                      {a.featured && <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Primary</span>}
                    </div>
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{a.name}</p>
                    <div className="flex items-center gap-1.5 text-xs text-[#F6C344] font-semibold mb-2">
                      <i className="ri-road-map-line"></i><span>{a.distance}</span>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed mb-1">{a.address}</p>
                    <p className="text-gray-400 text-xs leading-relaxed mb-3">{a.note}</p>
                    <a href={a.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                      <i className="ri-external-link-line text-sm"></i>{a.label}
                    </a>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-amber-500 mr-1.5"></i>
                  <strong>Passport required for all international flights.</strong> If your departure is within two weeks, standard processing won&apos;t make it in time — <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">expedited processing through Fast Passport Center</a> is your most reliable option.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}